import classNames from 'classnames';
import type {
  ChangeEvent,
  FormEvent,
  FunctionComponent,
  ReactNode,
} from 'react';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { identityBaseUrl } from '@trello/config';
import { intl } from '@trello/i18n';
import { Button } from '@trello/nachos/button';
import { showFlag } from '@trello/nachos/experimental-flags';
import { Textfield } from '@trello/nachos/textfield';
import type { PIIString } from '@trello/privacy';
import {
  convertToPIIString,
  dangerouslyConvertPrivacyString,
} from '@trello/privacy';
import type { ProfileTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import type {
  MemberFieldName,
  PrivacyOption,
} from 'app/src/components/MemberHome/MemberHomePrivacyLabel';
import { MemberHomePrivacyLabel } from 'app/src/components/MemberHome/MemberHomePrivacyLabel';
import {
  SectionMessage,
  SectionMessageAction,
} from 'app/src/components/SectionMessage';
import { useEnableMemberProfileSyncMutation } from './EnableMemberProfileSyncMutation.generated';
import type { SaveProfileError } from './SaveProfileErrors';
import { SaveProfileErrors } from './SaveProfileErrors';

import styles from './MemberHomeProfileTab.less';

// eslint-disable-next-line @trello/assets-alongside-implementation
import tacoPrivacySvg from 'resources/images/member-home/taco-privacy.svg';

interface MinimalLabelProps {
  field: MemberFieldName;
  isRequired?: boolean;
}

interface LabelProps extends MinimalLabelProps {
  privacy: PrivacyOption;
}

interface SaveErrorInfo {
  message: JSX.Element;
  id: string;
}

export interface MemberProfileParams {
  username?: PIIString;
  bio?: string;
}

const LABEL_NAMES = new Map<MemberFieldName, string>([
  [
    'bio',
    intl.formatMessage({
      id: 'templates.member_detail_profile_edit.bio',
      defaultMessage: 'Bio',
      description: 'Bio',
    }),
  ],
  [
    'username',
    intl.formatMessage({
      id: 'templates.member_detail_profile_edit.username',
      defaultMessage: 'Username',
      description: 'Username',
    }),
  ],
]);

const RequiredAsterisk: FunctionComponent = () => {
  return (
    <span
      aria-hidden="true"
      title="required"
      className={styles.requiredAsterisk}
    >
      *
    </span>
  );
};

const Label: FunctionComponent<LabelProps> = ({
  privacy,
  field,
  isRequired = false,
}) => {
  return (
    <div className={styles.inputLabel}>
      <label className={styles.text} htmlFor={field}>
        {LABEL_NAMES.get(field)}
        {isRequired ? <RequiredAsterisk /> : null}
      </label>
      <MemberHomePrivacyLabel
        privacy={privacy}
        className={styles.privacyLabel}
        field={field}
      />
    </div>
  );
};

const PublicLabel: FunctionComponent<MinimalLabelProps> = ({
  field,
  isRequired = false,
}) => {
  return (
    <Label field={field} privacy={'always-public'} isRequired={isRequired} />
  );
};

const SectionHeader: FunctionComponent<{ text: string }> = ({ text }) => {
  return (
    <>
      <h3 className={styles.sectionHeader}>{text}</h3>
    </>
  );
};

const InputWithError: FunctionComponent<{
  autoComplete?: string;
  className?: string;
  field: string;
  multiLine?: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  saveErrorInfo?: SaveErrorInfo;
  value?: PIIString;
  isRequired?: boolean;
}> = ({
  autoComplete,
  className,
  field,
  multiLine,
  onChange,
  saveErrorInfo,
  value,
  isRequired = false,
}) => {
  const attrs = {
    autoComplete,
    className,
    onChange,
    id: field,
    value: dangerouslyConvertPrivacyString(value),
    ...(saveErrorInfo
      ? {
          'aria-invalid': true,
          'aria-describedby': saveErrorInfo.id,
        }
      : {}),
  };

  return (
    <>
      {multiLine ? (
        <textarea required={isRequired} aria-required={isRequired} {...attrs} />
      ) : (
        <Textfield
          isInvalid={!!saveErrorInfo}
          name={field}
          isRequired={isRequired}
          aria-required={isRequired}
          {...attrs}
        />
      )}
      {saveErrorInfo?.message}
    </>
  );
};

const processSaveErrorMessages = (errors: SaveProfileError[] | undefined) => {
  const errorInfo: { [key in MemberFieldName]?: SaveErrorInfo } = {};

  if (errors) {
    errors.forEach((error) => {
      let errorMsg: ReactNode = null;
      let field: MemberFieldName;

      switch (error) {
        case SaveProfileErrors.UsernameTaken:
          errorMsg = (
            <FormattedMessage
              id="member profile error.username taken"
              defaultMessage="Username is taken"
              description="Username is taken"
            />
          );
          field = 'username';
          break;
        case SaveProfileErrors.UsernameIsReserved:
          errorMsg = (
            <FormattedMessage
              id="member profile error.username is reserved"
              defaultMessage="Username can’t contain “admin”, “auth”, “Trello”, or “Butler”."
              description="Username is reserved"
            />
          );
          field = 'username';
          break;
        case SaveProfileErrors.UsernameTooLong:
          errorMsg = (
            <FormattedMessage
              id="member profile error.username too long"
              defaultMessage="Username must be at most 100 characters"
              description="Username too long"
            />
          );
          field = 'username';
          break;
        case SaveProfileErrors.UsernameTooShort:
          errorMsg = (
            <FormattedMessage
              id="member profile error.username too short"
              defaultMessage="Username must be at least 3 characters"
              description="Username too short"
            />
          );
          field = 'username';
          break;
        case SaveProfileErrors.UsernameInvalid:
          errorMsg = (
            <FormattedMessage
              id="member profile error.username invalid"
              defaultMessage="Username is invalid: only lowercase letters, underscores, and numbers are allowed"
              description="Username invalid"
            />
          );
          field = 'username';
          break;
        case SaveProfileErrors.BioTooLong:
          errorMsg = (
            <FormattedMessage
              id="member profile error.bio too long"
              defaultMessage="Bio must be at most 16384 characters"
              description="Bio too long"
            />
          );
          field = 'bio';
          break;
        case SaveProfileErrors.Unknown:
        default:
          errorMsg = (
            <FormattedMessage
              id="member profile error.unknown"
              defaultMessage="Unknown error"
              description="Unknown error"
            />
          );
          field = 'unknown';
          break;
      }

      const id = 'SaveProfileError_Field_' + field;

      errorInfo[field] = {
        message: (
          <p id={id} className={styles.error}>
            {errorMsg}
          </p>
        ),
        id,
      };
    });
  }

  return errorInfo;
};

export interface MemberHomeProfileTabProps {
  saveMessage: string;
  member: {
    aaBlockSyncUntil?: string | null;
    bio: string;
    id: string;
    idEnterprise?: string | null;
    username: PIIString;
  };
  saveErrors: SaveProfileError[] | undefined;
  onSaveProfileError: (errors: SaveProfileError[] | string) => void;
  onSubmitMemberProfile: (profile: MemberProfileParams) => void;
}

const source = 'profileAndVisibilitySettingsScreen';

export const MemberHomeProfileTab: FunctionComponent<
  MemberHomeProfileTabProps
> = ({
  member: { aaBlockSyncUntil, bio, id, idEnterprise, username },
  onSaveProfileError,
  onSubmitMemberProfile,
  saveMessage,
  saveErrors,
}) => {
  const isProfileSyncBlocked = aaBlockSyncUntil;
  const isAaManaged = idEnterprise;

  const manageProfileUrl = `${identityBaseUrl}/manage-profile`;

  const [usernameState, setUsername] = useState(username);
  const [bioState, setBio] = useState(bio);
  const saveErrorInfo = processSaveErrorMessages(saveErrors);

  const [enableProfileSync] = useEnableMemberProfileSyncMutation();

  const isFormDisabled = saveMessage === 'saving';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateUsername = useCallback((e: any) => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const username = e.target.value;
    setUsername(username);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateBio = useCallback((e: any) => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const bio = e.target.value;
    setBio(bio);
  }, []);

  const submitUpdatedProfile = useCallback(
    (e: FormEvent) => {
      e.preventDefault();

      if (isFormDisabled) {
        return;
      }

      const profile: MemberProfileParams = {
        ...(usernameState && { username: usernameState }),
        bio: bioState || '',
      };

      const invalidInputs: SaveProfileError[] = [];

      if (!profile.username || profile.username.length < 3) {
        invalidInputs.push(SaveProfileErrors.UsernameTooShort);
      } else if (profile.username.length > 100) {
        invalidInputs.push(SaveProfileErrors.UsernameTooLong);
      }
      if (profile?.bio && profile.bio.length > 16384) {
        invalidInputs.push(SaveProfileErrors.BioTooLong);
      }
      if (invalidInputs.length) {
        onSaveProfileError(invalidInputs);
      } else {
        onSubmitMemberProfile(profile);
      }
    },
    [
      bioState,
      isFormDisabled,
      onSaveProfileError,
      onSubmitMemberProfile,
      usernameState,
    ],
  );

  const onClickGoToAa = useCallback(async () => {
    Analytics.sendClickedLinkEvent({
      linkName: 'unblockAaProfileSync',
      source,
    });

    const taskName = 'enable-member/profileSync';
    const traceId = Analytics.startTask({
      taskName,
      source,
    });

    try {
      await enableProfileSync({
        variables: {
          memberId: id,
        },
      });
      Analytics.taskSucceeded({
        taskName,
        traceId,
        source,
      });
      window.open(manageProfileUrl, '_blank');
    } catch (e) {
      Analytics.taskFailed({
        taskName,
        traceId,
        source,
        error: e,
      });
      showFlag({
        id: 'unblockAaProfileSyncError',
        title: intl.formatMessage({
          id: 'templates.member_detail_profile_edit.something-went-wrong-please-try-again',
          defaultMessage: 'Something went wrong. Please try again.',
          description: 'Something went wrong. Please try again.',
        }),
        appearance: 'error',
        isAutoDismiss: true,
      });
    }
  }, [enableProfileSync, id, manageProfileUrl]);

  useEffect(() => {
    Analytics.sendScreenEvent({
      name: source,
      attributes: {
        totalAccountEmails: 1,
        isAaMastered: true,
        isProfileSyncBlocked,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const atlassianProfile = (
    <a
      href={manageProfileUrl}
      key="atlassian-profile"
      target="_blank"
      rel="noopener noreferrer"
    >
      <FormattedMessage
        id="templates.member_home.atlassian_profile"
        defaultMessage="Atlassian profile"
        description="Atlassian profile"
      />
    </a>
  );
  const termsOfService = (
    <a
      href="https://www.atlassian.com/legal/cloud-terms-of-service"
      key="terms-of-service"
      target="_blank"
      rel="noopener noreferrer"
    >
      <FormattedMessage
        id="templates.member_home.terms_of_service"
        defaultMessage="Terms of Service"
        description="Terms of Service"
      />
    </a>
  );
  const privacyPolicy = (
    <a
      href="https://www.atlassian.com/legal/privacy-policy"
      key="privacy"
      target="_blank"
      rel="noopener noreferrer"
    >
      <FormattedMessage
        id="templates.member_home.privacy_policy"
        defaultMessage="Privacy Policy"
        description="Privacy Policy"
      />
    </a>
  );

  return (
    <div
      className={styles.memberProfileTabContainer}
      data-testid={getTestId<ProfileTestIds>('profile-tab-container')}
    >
      <img
        className={styles.tacoPrivacy}
        src={tacoPrivacySvg}
        alt=""
        role="presentation"
      />
      <h1 className={styles.profileHeader}>
        <FormattedMessage
          id="templates.member_home.manage-your-personal-information"
          defaultMessage="Manage your personal information"
          description="Manage your personal information"
        />
      </h1>
      <p className={classNames(styles.legalText)}>
        {isAaManaged ? (
          <FormattedMessage
            id="templates.member_home.legal_text_managed"
            // eslint-disable-next-line @trello/strict-react-intl
            defaultMessage="This is a managed Atlassian account. Edit your personal information and visibility settings through your {atlassianProfile}. To learn more, view our {termsOfService} or {privacyPolicy}."
            description="This is a managed Atlassian account. Edit your personal information and visibility settings through your {atlassianProfile}. To learn more, view our {termsOfService} or {privacyPolicy}."
            values={{
              atlassianProfile,
              termsOfService,
              privacyPolicy,
            }}
          />
        ) : (
          <FormattedMessage
            id="templates.member_home.legal_text_mastered"
            // eslint-disable-next-line @trello/strict-react-intl
            defaultMessage="This is an Atlassian account. Edit your personal information and visibility settings through your {atlassianProfile}. To learn more, view our {termsOfService} or {privacyPolicy}."
            description="This is an Atlassian account. Edit your personal information and visibility settings through your {atlassianProfile}. To learn more, view our {termsOfService} or {privacyPolicy}."
            values={{
              atlassianProfile,
              termsOfService,
              privacyPolicy,
            }}
          />
        )}
      </p>
      <SectionHeader
        text={intl.formatMessage({
          id: 'templates.member_detail_profile_edit.about',
          defaultMessage: 'About',
          description: 'About',
        })}
      />
      {isProfileSyncBlocked && (
        <div className={styles.profileSyncSectionMessage}>
          <SectionMessage
            title={intl.formatMessage({
              id: 'templates.member_detail_profile_edit.to-edit-your-full-name-initials-and-profile-photo',
              defaultMessage:
                'To edit your full name, initials, and profile photo, go to your Atlassian profile.',
              description:
                'To edit your full name, initials, and profile photo, go to your Atlassian profile.',
            })}
            actions={[
              <SectionMessageAction
                onClick={onClickGoToAa}
                key={'go-to-atlassian-profile'}
              >
                <FormattedMessage
                  id="templates.member_detail_profile_edit.go-to-your-atlassian-profile"
                  defaultMessage="Go to your Atlassian profile"
                  description="Go to your Atlassian profile"
                />
              </SectionMessageAction>,
            ]}
          >
            <FormattedMessage
              id="templates.member_detail_profile_edit.your-profile-information-and-visibility-settings"
              defaultMessage="Your profile information and visibility settings are managed in your Atlassian profile."
              description="Your profile information and visibility settings are managed in your Atlassian profile."
            />
          </SectionMessage>
        </div>
      )}
      <div aria-hidden="true">
        <FormattedMessage
          id="templates.member_detail_profile_edit.required-fields"
          defaultMessage="Required fields are marked with an asterisk"
          description="Required fields are marked with an asterisk"
        />
        <RequiredAsterisk />
      </div>
      <div className={classNames(styles.aboutContent)}>
        <form
          className={styles.leftColumn}
          onSubmit={submitUpdatedProfile}
          data-testid={getTestId<ProfileTestIds>('profile-form')}
        >
          <PublicLabel field={'username'} isRequired={true} />
          <InputWithError
            onChange={updateUsername}
            value={usernameState}
            field={'username'}
            autoComplete="username"
            saveErrorInfo={saveErrorInfo['username']}
            isRequired={true}
          />

          <PublicLabel field={'bio'} />
          <InputWithError
            onChange={updateBio}
            className={classNames(styles.bioTextArea)}
            value={convertToPIIString(bioState)}
            field={'bio'}
            multiLine
            saveErrorInfo={saveErrorInfo['bio']}
          />
          <Button
            appearance="primary"
            isDisabled={isFormDisabled}
            className={styles.profileSaveButton}
            type="submit"
          >
            <FormattedMessage
              id="templates.member_detail_profile_edit.save"
              defaultMessage="Save"
              description="Save"
            />
          </Button>
        </form>
      </div>
    </div>
  );
};
